import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Trash2, KeyRound, ShieldAlert } from 'lucide-react';

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'admin' });
  const [passwordChange, setPasswordChange] = useState({ userId: '', password: '' });

  const loadUsers = async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async () => {
    try {
      await adminAPI.createUser(newUser);
      toast.success('Benutzer erstellt');
      setShowCreate(false);
      setNewUser({ username: '', email: '', password: '', role: 'admin' });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const handleToggleActive = async (user) => {
    try {
      await adminAPI.updateUser(user.id, { active: !user.active });
      toast.success(`Benutzer ${!user.active ? 'aktiviert' : 'deaktiviert'}`);
      loadUsers();
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try {
      await adminAPI.deleteUser(id);
      toast.success('Benutzer gelöscht');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const handleChangePassword = async () => {
    if (!passwordChange.password || passwordChange.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    try {
      await adminAPI.changePassword(passwordChange.userId, passwordChange.password);
      toast.success('Passwort geändert');
      setPasswordChange({ userId: '', password: '' });
    } catch { toast.error('Fehler'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="user-manager">
      <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="password-change-hint">
        <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-sm font-body text-amber-800">
          Bitte ändern Sie nach dem ersten Login das Standardpasswort. Verwenden Sie ein sicheres Passwort mit mindestens 8 Zeichen.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Benutzerverwaltung</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Admin-Benutzer verwalten</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body" data-testid="create-user-button">
              <UserPlus className="w-4 h-4 mr-2" /> Neuer Benutzer
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Neuen Benutzer erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="font-body text-sm">Benutzername</Label>
                <Input value={newUser.username} onChange={(e) => setNewUser(p => ({ ...p, username: e.target.value }))} className="mt-1 rounded-sm font-body" data-testid="new-user-username" />
              </div>
              <div>
                <Label className="font-body text-sm">E-Mail</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))} className="mt-1 rounded-sm font-body" data-testid="new-user-email" />
              </div>
              <div>
                <Label className="font-body text-sm">Passwort</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))} className="mt-1 rounded-sm font-body" data-testid="new-user-password" />
              </div>
              <div>
                <Label className="font-body text-sm">Rolle</Label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser(p => ({ ...p, role: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm font-body text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                  data-testid="new-user-role"
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body" data-testid="submit-create-user">
                Erstellen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-body text-xs">Benutzername</TableHead>
                  <TableHead className="font-body text-xs">E-Mail</TableHead>
                  <TableHead className="font-body text-xs">Rolle</TableHead>
                  <TableHead className="font-body text-xs">Aktiv</TableHead>
                  <TableHead className="font-body text-xs">Letzter Login</TableHead>
                  <TableHead className="font-body text-xs text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-body text-sm font-medium">{user.username}</TableCell>
                    <TableCell className="font-body text-sm text-slate-600">{user.email}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-body text-xs">{user.role}</Badge></TableCell>
                    <TableCell>
                      <Switch
                        checked={user.active}
                        onCheckedChange={() => handleToggleActive(user)}
                        data-testid={`user-toggle-${user.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-body text-xs text-slate-500">
                      {user.last_login ? new Date(user.last_login).toLocaleString('de-DE') : 'Nie'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => setPasswordChange({ userId: user.id, password: '' })} data-testid={`user-password-${user.id}`}>
                              <KeyRound className="w-3 h-3" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm">
                            <DialogHeader>
                              <DialogTitle className="font-heading">Passwort ändern: {user.username}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-2">
                              <Input
                                type="password"
                                placeholder="Neues Passwort (min. 8 Zeichen)"
                                value={passwordChange.userId === user.id ? passwordChange.password : ''}
                                onChange={(e) => setPasswordChange({ userId: user.id, password: e.target.value })}
                                className="rounded-sm font-body"
                              />
                              <Button onClick={handleChangePassword} className="w-full bg-[#0E518D] text-white rounded-sm font-body">
                                Passwort ändern
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`user-delete-${user.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
